import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { FilterOption } from 'models/Filters';
import { BODY_CONTENT_GENERATE } from './components';
import useQueryParams from 'hooks/useQueryParams';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomNdaSigner } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import DataRoomNdaTableProviderContext from '../../providers/DataRoomNdaTableProvider/DataRoomNdaTableProvider.context';
import {
  FIXED_TABLE_COLUMNS,
  FIXED_COLUMN_TRANSLATIONS,
  FIXED_COLUMN_SORTING_VALUES,
  FIXED_COLUMN_SIZE,
  DYNAMIC_TABLE_COLUMNS,
  DYNAMIC_COLUMN_SIZE,
  SORT_VALUES,
  NDA_SORT_CLEAR_FIELDS,
} from './components/data';
import {
  getIsDesc,
  getSortBy,
  getSortValue,
  getSortedOptionsFromStrings,
} from 'utils';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import { getIsSignerSelected } from '../../utils';
import { Checkbox } from 'ncoded-component-library';
import DataRoomNdaBulkActions from '../DataRoomNdaBulkActions';

import './DataRoomNdasTable.styles.scss';

const getIsNdaCell = (column: string) =>
  !Object.values(FIXED_TABLE_COLUMNS).includes(column);

type DataRoomNdasTableProps = {
  className?: string;
  isLoading: boolean;
  totalPages: number;
  totalItems?: number;
  items: DataRoomNdaSigner[];
  emptyMessage?: string;
};

const DataRoomNdasTable: React.FC<DataRoomNdasTableProps> = (props) => {
  const { className, isLoading, totalPages, totalItems, items, emptyMessage } =
    props;

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { dataRoomNdas } = useContext(DataRoomNdaContext);
  const {
    selected,
    isAllSelected,
    openNdaDrawer,
    openNdaModal,
    selectAll,
    removeAll,
    updateSelected,
  } = useContext(DataRoomNdaTableProviderContext);

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const {
    params: { sort, dataRoomNdaId },
    setMultipleQueryParams,
  } = useQueryParams();

  const { t } = useTranslation();

  const classes = classNames('data-room-ndas-table', className);

  const tableColumns = useMemo(
    () => [
      FIXED_TABLE_COLUMNS.CHECKBOX,
      FIXED_TABLE_COLUMNS.NAME,
      FIXED_TABLE_COLUMNS.EMAIL,
      ...dataRoomNdas.map(({ _id }) => _id),
    ],
    [dataRoomNdas],
  );

  const sortingValues = useMemo(
    () =>
      dataRoomNdas.reduce(
        (acc, { _id }) => ({
          ...acc,
          [_id]: _id,
        }),
        FIXED_COLUMN_SORTING_VALUES,
      ),
    [dataRoomNdas],
  );

  const getNameOptions = useCallback(
    async (searchString: string) => {
      const { data } = await dataRoomApi.nda.getNdaSignerNameOptions(
        dataRoomId,
        searchString,
      );

      return getSortedOptionsFromStrings(data.items);
    },
    [dataRoomId],
  );

  const getEmailOptions = useCallback(
    async (searchString: string) => {
      const { data } = await dataRoomApi.nda.getNdaSignerEmailOptions(
        dataRoomId,
        searchString,
      );

      return getSortedOptionsFromStrings(data.items);
    },
    [dataRoomId],
  );

  const columnFilterOptions = useMemo(
    () => ({
      [FIXED_TABLE_COLUMNS.NAME]: {
        options: getMultiSelectSearchableFilter(
          t('name'),
          FIXED_TABLE_COLUMNS.NAME,
          FIXED_TABLE_COLUMNS.NAME,
          getNameOptions,
        ) as FilterOption,
      },
      [FIXED_TABLE_COLUMNS.EMAIL]: {
        options: getMultiSelectSearchableFilter(
          t('email'),
          FIXED_TABLE_COLUMNS.EMAIL,
          FIXED_TABLE_COLUMNS.EMAIL,
          getEmailOptions,
        ) as FilterOption,
      },
    }),
    [getMultiSelectSearchableFilter, getNameOptions, getEmailOptions, t],
  );

  const filterOptions = useMemo(
    () => Object.values(columnFilterOptions).map(({ options }) => options),
    [columnFilterOptions],
  );

  const sortOptions = useMemo(
    () => [
      { value: SORT_VALUES.NAME, label: t('name') },
      { value: SORT_VALUES.EMAIL, label: t('email') },
      {
        value: SORT_VALUES.STATUS,
        label: dataRoomNdas.find(({ _id }) => _id === dataRoomNdaId)?.name,
      },
    ],
    [dataRoomNdas, dataRoomNdaId, t],
  );

  const columnsSize = useMemo(
    () => ({
      ...FIXED_COLUMN_SIZE,
      ...dataRoomNdas.reduce(
        (acc, { _id }) => ({
          ...acc,
          [_id]: DYNAMIC_COLUMN_SIZE,
        }),
        {},
      ),
    }),
    [dataRoomNdas],
  );

  const headerContent = useMemo(
    () => ({
      [FIXED_TABLE_COLUMNS.CHECKBOX]: () => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isAllSelected}
            onChange={isAllSelected ? removeAll : selectAll}
          />
        </div>
      ),
      ...dataRoomNdas.reduce(
        (acc, { _id, name }) => ({
          ...acc,
          [_id]: <p className="nda-header">{name}</p>,
        }),
        {},
      ),
    }),
    [dataRoomNdas, isAllSelected, removeAll, selectAll],
  );

  const renderedContent = useMemo(
    () =>
      tableColumns.reduce(
        (acc, column) => ({
          ...acc,
          [column]: (signer: DataRoomNdaSigner) => {
            const isNdaCell = getIsNdaCell(column);

            const colComponent = isNdaCell ? DYNAMIC_TABLE_COLUMNS.NDA : column;
            const dataRoomNdaId = isNdaCell ? column : undefined;

            return BODY_CONTENT_GENERATE[colComponent]({
              signer,
              dataRoomNdaId,
              isSelected: getIsSignerSelected(selected, signer),
              updateSelected,
            });
          },
        }),
        {},
      ),
    [tableColumns, selected, updateSelected],
  );

  const activeSort = useMemo(
    () => (dataRoomNdaId ? getSortValue(dataRoomNdaId, getIsDesc(sort)) : sort),
    [sort, dataRoomNdaId],
  );

  const updateSortValues = useCallback(
    (sort) => {
      const isDesc = getIsDesc(sort);
      const value = getSortBy(sort);

      const isFieldSort =
        value === SORT_VALUES.NAME || value === SORT_VALUES.EMAIL;

      setMultipleQueryParams({
        dataRoomNdaId: isFieldSort ? null : value,
        sort: isFieldSort
          ? getSortValue(value, isDesc)
          : getSortValue(SORT_VALUES.STATUS, isDesc),
      });
    },
    [setMultipleQueryParams],
  );

  const onSignerRowClick = useCallback(
    (signer: DataRoomNdaSigner) => {
      const { dataRoomNdaSignings } = signer;

      if (!dataRoomNdaSignings.length) {
        return;
      }

      if (dataRoomNdaSignings.length === 1) {
        openNdaDrawer(signer, dataRoomNdaSignings[0]);

        return;
      }

      openNdaModal(signer);
    },
    [openNdaDrawer, openNdaModal],
  );

  return (
    <div className={classes}>
      <div className="data-room-ndas-table__toolbar">
        <ActiveFilterSortPills
          filterOptions={filterOptions}
          sortOptions={sortOptions}
          sortClearFields={NDA_SORT_CLEAR_FIELDS}
        />
        <DataRoomNdaBulkActions />
      </div>

      <ReorderResizeTable
        columns={tableColumns as Array<keyof DataRoomNdaSigner>}
        data={items}
        isDragDisabled
        isResizeDisabled
        totalPages={totalPages}
        totalItems={totalItems}
        isDataLoading={isLoading}
        sortingValues={sortingValues}
        columnFilterOptions={columnFilterOptions}
        customSizes={columnsSize}
        headingTranslations={FIXED_COLUMN_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        tableHeaderContentTransformations={headerContent}
        sort={activeSort}
        emptyMessage={emptyMessage}
        updateSort={updateSortValues}
        onRowClick={onSignerRowClick}
        getIsRowSelected={(signer) => getIsSignerSelected(selected, signer)}
      />
    </div>
  );
};

export default DataRoomNdasTable;
